import { useRetailProviderOffers } from "@/composition/use-retail-provider-offers"
import type { ProviderKey } from "@versatile-credit-apply/omni-apply-client"
import {
  useApplicationStore,
  useCurrentApplyResponse,
} from "@versatile-credit-apply/sdk"
import type { RouteRecordRaw } from "vue-router"

export const SynchronyDecisionRoutes: RouteRecordRaw[] = [
  {
    name: "synchrony-apply-approved",
    path: "approved",
    component: () => import("./components/SynchronyApproved.vue"),
  },
  {
    name: "synchrony-apply-pending",
    path: "pending",
    component: () => import("./components/SynchronyPending.vue"),
    beforeEnter: (to) => {
      const providerKey = to.params.providerKey as ProviderKey
      const applyResponse = useCurrentApplyResponse(() => providerKey)

      if (
        applyResponse.value?.pendingReasons?.find(
          (reason) => reason === "complete_via_external_url"
        )
      ) {
        return {
          path: to.path.replace("pending", "pending/complete_via_external_url"),
          replace: true,
        }
      }

      return true
    },
  },
  {
    name: "synchrony-apply-prequalified",
    path: "prequalified",
    beforeEnter: (to) => {
      const plans = useRetailProviderOffers()

      if (plans.value.length > 1) {
        return {
          path: to.path.replace("/prequalified", "/explore-offers"),
          replace: true,
        }
      }
      return true
    },
    component: async () => import("./components/SynchronyPrequalified.vue"),
    meta: {
      step: "applicant",
      next: "../terms",
    },
  },
  {
    name: "synchrony-apply-declined",
    path: "declined",
    component: () => import("./components/SynchronyDeclined.vue"),
  },
  {
    name: "synchrony-apply-error",
    path: "error",
    component: () => import("./components/SynchronyError.vue"),
  },
  {
    name: "synchrony-apply-nooffer",
    path: "nooffer",
    component: () => import("./components/SynchronyNoOffer.vue"),
  },
]

const SynchronyOfferRoutes: RouteRecordRaw[] = [
  {
    name: "synchrony-select-offer",
    path: "",
    props: true,
    component: () => import("./pages/SynchronySelectOfferPage.vue"),
    meta: {
      next: "../terms",
    },
  },
  {
    name: "synchrony-review-offer",
    path: "review",
    props: true,
    component: () => import("./pages/SynchronyReviewOfferPage.vue"),
  },
]

export const SynchronyApplyRoutes: RouteRecordRaw[] = [
  {
    name: "synchrony-apply-routes",
    path: "synchrony_standard",
    children: [
      {
        name: "synchrony-apply-start",
        path: "start/:mode",
        component: () => import("./pages/SynchronyIntroView.vue"),
        meta: {
          step: "applicant",
        },
      },
      {
        name: "synchrony-terms",
        path: "terms",
        component: () => import("./pages/SynchronyTermsAndConditionsPage.vue"),
        meta: {
          next: () => {
            const store = useApplicationStore()
            return store.applyMode !== "full" ? "accept-offer" : "submit"
          },
          step: "terms",
        },
      },
      {
        name: "synchrony-apply-decisions",
        path: "decision",
        component: () => import("./pages/SynchronyDecisionPage.vue"),
        meta: {
          preventBack: true,
          step: "decision",
        },
        children: SynchronyDecisionRoutes,
      },
      {
        name: "synchrony-offers",
        path: "offer/:planCode",
        props: true,
        meta: {
          step: "decision",
        },
        children: SynchronyOfferRoutes,
      },
      {
        name: "synchrony-pending-external-application",
        path: "decision/pending/complete_via_external_url",
        component: () => import("./pages/SynchronyMPQLinkPage.vue"),
        meta: {
          step: "decision",
        },
      },
      {
        name: "synchrony-hosted-link",
        path: "hosted-link",
        meta: {
          stepper: {
            id: "hosted-link",
          },
        },
        children: [
          {
            name: "synchrony-hosted-link-send",
            path: "",
            component: () => import("./pages/SynchronySendHostedLinkPage.vue"),
            meta: {
              step: "applicant",
              next: "hosted-link/sent",
            },
          },
          {
            name: "synchrony-hosted-link-sent",
            path: "sent",
            component: () => import("./pages/SynchronyHostedLinkSentPage.vue"),
            meta: {
              step: "link-sent",
            },
          },
        ],
      },
    ],
  },
]
